import React from "react";
import toast from "react-hot-toast";
import { CloseLoopRequestFilter } from "../../../components/modals/close-loop-filter/types";
import { useExportCloseLoopLettersMutation } from "../../../services/headofficeadmin";
import { openConfirmModal } from "../../../components/modals/ConfirmModal";

export type CloseLoopExportParams = {
    isFetching: boolean;
    isError: boolean;
    filter: CloseLoopRequestFilter;
    excludeIds: string[];
};

export type CloseLoopExportResult = {
    exportLetters: (closeLoop: boolean) => Promise<void>;
};

type MissingPlaceholderError = {
    exceptions: {
        masterAccountId: string;
        masterAccountName: string;
        placeholders: string[];
    }[];
};

const useCloseLoopExport = ({ isFetching, isError, filter, excludeIds }: CloseLoopExportParams): CloseLoopExportResult => {
    const [exportTrigger] = useExportCloseLoopLettersMutation();

    /**
     * Displays a modal containing details of Master Accounts with incomplete data causing export to fail
     * @param err Error returned from service
     */
    const raiseExceptions = async (err: { data: string; }) => {
        console.log(err);

        const errorData: string = err.data;
        const data = JSON.parse(errorData);

        const typedData = data as MissingPlaceholderError;
        console.log(typedData);

        if (typedData.exceptions != null && typedData?.exceptions.length > 0) {
            const { exceptions } = typedData;
            await openConfirmModal({
                allowConfirmButton: false,
                dialogTitle: "Export Failed",
                message: <>
                    <p>Export failed due to the following account(s) missing required information:</p>
                    <ul>
                        {exceptions.map(e => {
                            const { masterAccountId, masterAccountName, placeholders } = e;

                            return <li>
                                <a href={`/client/${masterAccountId}`}>{masterAccountName}</a>: {placeholders.join(", ")}
                            </li>;
                        })}
                    </ul>
                </>
            });
        }

        // Rethrow so the toast shows the error response
        throw err;
    };

    const exportAndOpen = async (closeLoop: boolean) => {
        try {
            const res = await exportTrigger({
                filter,
                excludeIds,
                closeLoop
            }).unwrap();

            const { blob, filename } = res;
            const url = window.URL.createObjectURL(blob);

            // Create a temporary link to trigger the download with set filename
            const tempAnchor = document.createElement('a');
            tempAnchor.href = url;
            tempAnchor.download = filename;
            document.body.appendChild(tempAnchor);
            tempAnchor.click();

            // Clean up the URL object
            document.body.removeChild(tempAnchor);
            window.URL.revokeObjectURL(url);
        } catch (err: unknown) {
            await raiseExceptions(err as { data: string; });
        }
    };

    const exportLetters = (closeLoop: boolean) => {
        if (isFetching || isError)
            return Promise.reject();

        return toast.promise(
            exportAndOpen(closeLoop),
            {
                loading: "Preparing Close Loop export...",
                success: "Close Loop export complete.",
                error: "Failed to export Close Loop data."
            }
        );
    };

    return { exportLetters };
};

export default useCloseLoopExport;